import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';

import useApi from 'common/useApi';
import httpClient from 'common/useApi/httpClient';
import INotification from 'common/types/INotification';

import Icons from 'assets/images/Icons';

import './NotificationBell.scss';

const NotificationBell = ({ onNavigate }: { onNavigate?: () => void }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: unread } = useApi('get', '/api/notifications/unread-count');
  const { data: list, isLoading } = useApi(
    'getList',
    '/api/notifications',
    { page: 1 },
    { enabled: open },
  );

  const unreadCount = unread?.unread ?? 0;
  const notifications: INotification[] = list?.results ?? [];

  // Close on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
  };

  const markAllRead = () => {
    httpClient.post('/api/notifications/mark-all-read/').then(invalidate);
  };

  const dismiss = (notification: INotification) => {
    httpClient.delete(`/api/notifications/${notification.id}/`).then(invalidate);
  };

  const openNotification = (notification: INotification) => {
    const finish = () => {
      invalidate();
      setOpen(false);
      onNavigate?.();
      if (notification.link) navigate(`/${notification.link.replace(/^\//, '')}`);
    };

    if (notification.read_at) {
      finish();
      return;
    }
    httpClient
      .post(`/api/notifications/${notification.id}/mark-read/`)
      .then(finish)
      .catch(finish);
  };

  return (
    <div className='notification-bell' ref={ref}>
      <button
        type='button'
        className='bell-trigger'
        aria-label='Notifications'
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icons name='Bell' />
        {unreadCount > 0 && (
          <span className='bell-badge'>{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
      {open && (
        <div className='notification-dropdown'>
          <div className='notification-header'>
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button type='button' className='mark-all' onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>
          <div className='notification-list'>
            {isLoading && <div className='notification-empty'>Loading…</div>}
            {!isLoading && notifications.length === 0 && (
              <div className='notification-empty'>You&apos;re all caught up.</div>
            )}
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={classNames({
                  'notification-item': true,
                  unread: !notification.read_at,
                  [notification.level]: true,
                })}
              >
                <button
                  type='button'
                  className='notification-item-open'
                  onClick={() => openNotification(notification)}
                >
                  <div className='notification-title'>{notification.title}</div>
                  {notification.body && (
                    <div className='notification-body'>{notification.body}</div>
                  )}
                  <div className='notification-meta'>
                    {notification.subaccount_username && (
                      <span className='notification-chip'>{notification.subaccount_username}</span>
                    )}
                    <span className='notification-time'>
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </span>
                  </div>
                </button>
                <button
                  type='button'
                  className='notification-dismiss'
                  aria-label='Dismiss notification'
                  onClick={() => dismiss(notification)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            type='button'
            className='notification-see-all'
            onClick={() => {
              setOpen(false);
              onNavigate?.();
              navigate('/notifications');
            }}
          >
            See all notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
